import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { DeviceTrendPoints } from '@repo/sdk/models/components';

import { FEATURE_FLAGS } from '@/constants';
import { useCurrentFacility } from '@/hooks/use-current-facility';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useGaiaClient } from '@/hooks/use-gaia-client';
import { listDeviceTrendPointsQuery } from '@/queries/gaia-api/list-device-trend-points-query';

import type { CompressorAlarmRefs } from '../compressor-alarm-code.types';
import { findCompressorAlarmRefs } from '../compressor-alarm-code.utils';

/**
 * The facility's trend points (one cached request for the whole page), or nothing while
 * the `gaia-bulk-fault-reset` flag is off.
 *
 * UI-only stopgap so the Alarm Code control previews without a BFF deploy: the card's config
 * doesn't carry the alarm code points, so they are found by key in these. Replaced by refs
 * on the device's SummaryConfig from the BFF in the PR stacked on this one.
 */
const useFacilityTrendPoints = (): DeviceTrendPoints[] | undefined => {
  const isEnabled = useFeatureFlag(FEATURE_FLAGS.GAIA_BULK_FAULT_RESET);
  const client = useGaiaClient();
  const { agentId } = useCurrentFacility();

  const { data } = useQuery({
    ...listDeviceTrendPointsQuery({ agentId, client }),
    enabled: isEnabled && !!agentId,
  });

  return data as DeviceTrendPoints[] | undefined;
};

/** A device's alarm code points, or nothing while the flag is off or the device lacks them. */
export const useCompressorAlarmRefs = (deviceId: string): CompressorAlarmRefs | undefined => {
  const trendPoints = useFacilityTrendPoints();

  return useMemo(
    () =>
      findCompressorAlarmRefs(
        trendPoints?.find((device) => device.deviceId === deviceId)?.allDisplayRefs
      ),
    [trendPoints, deviceId]
  );
};

/**
 * The alarm code points of every device that has them, or of `deviceIds` only; none while
 * the flag is off.
 */
export const useAllCompressorAlarmRefs = (deviceIds?: string[]): CompressorAlarmRefs[] => {
  const trendPoints = useFacilityTrendPoints();
  const deviceKey = deviceIds?.join('\n');

  return useMemo(() => {
    const wanted = deviceKey === undefined ? undefined : new Set(deviceKey.split('\n'));
    return (trendPoints ?? []).flatMap((device) => {
      if (wanted && !wanted.has(device.deviceId)) return [];
      const refs = findCompressorAlarmRefs(device.allDisplayRefs);
      return refs ? [refs] : [];
    });
  }, [trendPoints, deviceKey]);
};
